import React, { useState } from 'react';
import { Settings, Users, Database, FileText, Shield, ChevronDown } from 'lucide-react';
import { TODAY } from '@/data/financial-systems';

const subMins = (d: Date, m: number) => new Date(d.getTime() - m * 60 * 1000);
const subHours = (d: Date, h: number) => new Date(d.getTime() - h * 60 * 60 * 1000);
const subDays = (d: Date, days: number) => new Date(d.getTime() - days * 24 * 60 * 60 * 1000);
const fmtDateTime = (d: Date) => d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

type ActivityType = 'configuration' | 'user_management' | 'data' | 'report' | 'security';

interface ActivityEntry {
  timestamp: Date;
  user: string;
  role: string;
  type: ActivityType;
  action: string;
  detail: string;
  ipAddress: string;
}

const ACTIVITY_LOG: ActivityEntry[] = [
  { timestamp: subMins(TODAY, 8), user: 'Sarah Mitchell', role: 'Finance Manager', type: 'report', action: 'Report exported', detail: 'Cash Flow Statement exported as PDF (245 KB)', ipAddress: '10.0.1.42' },
  { timestamp: subMins(TODAY, 22), user: 'David Chen', role: 'System Administrator', type: 'configuration', action: 'Terminology updated', detail: 'Cash duration term changed: "coverage" → "runway"', ipAddress: '10.0.1.10' },
  { timestamp: subMins(TODAY, 35), user: 'Linda Kowalski', role: 'CFO', type: 'data', action: 'Scenario approved', detail: 'Scenario "Cost Cutting Plan 2026" approved and set to Active', ipAddress: '10.0.1.55' },
  { timestamp: subHours(TODAY, 1), user: 'Sarah Mitchell', role: 'Finance Manager', type: 'data', action: 'Recurring transaction updated', detail: 'Office lease — amount updated £4,200 → £4,450 (renewal)', ipAddress: '10.0.1.42' },
  { timestamp: subHours(TODAY, 2), user: 'David Chen', role: 'System Administrator', type: 'user_management', action: 'Role permission modified', detail: 'Finance Manager → manage_recurring_transactions permission added', ipAddress: '10.0.1.10' },
  { timestamp: subHours(TODAY, 3), user: 'Edna Foster', role: 'Accounts Assistant', type: 'data', action: 'Transaction categorised', detail: '14 uncategorised transactions assigned to categories', ipAddress: '10.0.1.38' },
  { timestamp: subHours(TODAY, 4), user: 'Sarah Mitchell', role: 'Finance Manager', type: 'report', action: 'Report scheduled', detail: 'Variance Analysis report — weekly schedule configured (Mondays 08:00)', ipAddress: '10.0.1.42' },
  { timestamp: subHours(TODAY, 5), user: 'David Chen', role: 'System Administrator', type: 'security', action: 'Lockout protection triggered', detail: 'Attempt to remove last manage_roles_permissions holder blocked by system', ipAddress: '10.0.1.10' },
  { timestamp: subDays(TODAY, 1), user: 'Linda Kowalski', role: 'CFO', type: 'data', action: 'Scenario created', detail: 'New scenario "Apprenticeship Levy Optimisation" created as Draft', ipAddress: '10.0.1.55' },
  { timestamp: subDays(TODAY, 1), user: 'Sarah Mitchell', role: 'Finance Manager', type: 'data', action: 'Excel import completed', detail: 'Budget data imported — 156 rows, 0 errors', ipAddress: '10.0.1.42' },
  { timestamp: subDays(TODAY, 1), user: 'David Chen', role: 'System Administrator', type: 'configuration', action: 'Data source sync configured', detail: 'Dimensions Cloud sync interval updated to 60 minutes', ipAddress: '10.0.1.10' },
  { timestamp: subDays(TODAY, 2), user: 'Marcus Patel', role: 'Regional Finance Director', type: 'report', action: 'Report exported', detail: 'Scenario Comparison report exported as Excel (892 KB)', ipAddress: '10.0.1.61' },
  { timestamp: subDays(TODAY, 2), user: 'David Chen', role: 'System Administrator', type: 'user_management', action: 'User added', detail: 'Emma Thompson added — Accounts Assistant role assigned', ipAddress: '10.0.1.10' },
  { timestamp: subDays(TODAY, 3), user: 'Sarah Mitchell', role: 'Finance Manager', type: 'data', action: 'Scenario submitted for review', detail: 'Scenario "Win BigCo Contract" submitted to Finance Manager for review', ipAddress: '10.0.1.42' },
];

const typeConfig: Record<ActivityType, { icon: React.ReactNode; label: string; colour: string }> = {
  configuration: { icon: <Settings className="h-3.5 w-3.5" />, label: 'Configuration', colour: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800' },
  user_management: { icon: <Users className="h-3.5 w-3.5" />, label: 'User Management', colour: 'text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-800' },
  data: { icon: <Database className="h-3.5 w-3.5" />, label: 'Data', colour: 'text-teal-600 bg-teal-50 border-teal-200 dark:bg-teal-950/30 dark:border-teal-800' },
  report: { icon: <FileText className="h-3.5 w-3.5" />, label: 'Report', colour: 'text-green-600 bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' },
  security: { icon: <Shield className="h-3.5 w-3.5" />, label: 'Security', colour: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800' },
};

const ACTIVITY_TYPES: ActivityType[] = ['configuration', 'user_management', 'data', 'report', 'security'];

export const SystemActivityLogPreview: React.FC = () => {
  const [filter, setFilter] = useState<ActivityType | 'all'>('all');
  const reportDate = TODAY.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const filtered = filter === 'all' ? ACTIVITY_LOG : ACTIVITY_LOG.filter(e => e.type === filter);

  const counts: Record<ActivityType, number> = {
    configuration: 0, user_management: 0, data: 0, report: 0, security: 0,
  };
  ACTIVITY_LOG.forEach(e => counts[e.type]++);

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">System Activity Log</h3>
          <p className="text-sm text-muted-foreground">BuildRight Construction Ltd · Last 30 days · Configuration, permissions, data and report activity</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Activity type summary */}
      <div className="grid grid-cols-5 gap-2">
        {ACTIVITY_TYPES.map(type => {
          const cfg = typeConfig[type];
          return (
            <button
              key={type}
              onClick={() => setFilter(filter === type ? 'all' : type)}
              className={`rounded-lg border p-3 text-center transition-all hover:opacity-80 ${filter === type ? cfg.colour : 'bg-muted/40 border-muted'}`}
            >
              <div className={`flex justify-center mb-1 ${filter === type ? '' : 'text-muted-foreground'}`}>
                {cfg.icon}
              </div>
              <p className="text-lg font-bold">{counts[type]}</p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">{cfg.label}</p>
            </button>
          );
        })}
      </div>

      {filter !== 'all' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtered by:</span>
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${typeConfig[filter].colour}`}>
            {typeConfig[filter].icon}
            {typeConfig[filter].label}
          </span>
          <button onClick={() => setFilter('all')} className="text-xs text-muted-foreground hover:text-foreground underline">
            Clear filter
          </button>
        </div>
      )}

      {/* Activity log table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">
                <span className="flex items-center gap-1">Timestamp <ChevronDown className="h-3 w-3" /></span>
              </th>
              <th className="px-4 py-3 text-left font-semibold">User</th>
              <th className="px-4 py-3 text-left font-semibold">Type</th>
              <th className="px-4 py-3 text-left font-semibold">Action</th>
              <th className="px-4 py-3 text-left font-semibold">Detail</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry, i) => {
              const cfg = typeConfig[entry.type];
              return (
                <tr key={i} className="border-b hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDateTime(entry.timestamp)}
                  </td>
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium">{entry.user}</p>
                    <p className="text-xs text-muted-foreground">{entry.role}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.colour}`}>
                      {cfg.icon}
                      {cfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-sm font-medium">{entry.action}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{entry.detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Generated by Access Cash Forecaster · {reportDate} · Showing last 30 days · {ACTIVITY_LOG.length} events recorded · For audit and compliance use
      </p>
    </div>
  );
};
